import React, { useCallback, useState, useEffect, useMemo } from "react";
import styled from '@emotion/styled';
import { isMobile } from 'react-device-detect';
import Card from '../components/Ui/Card.jsx';
import { ServerSidePagingTable } from "../components/Ui/Table.jsx";
import { fetchReports, fetchPdf } from "../http/reporting.js";
import { fetchLinkedAccounts } from "../http/account.js";
import { Alert } from "../components/Ui/Alert.jsx";
import { formatDateToDDMMYYYY } from "../components/Helpers/dateFns.js";

const CenterContainer = styled.div`
  display: flex;
  justify-content: center;
  height: 100vh;
  width: 100%;
`;

const FormContainer = styled.div`
  border-radius: 5px;
  background-color: #fff;
  width: ${isMobile ? '100%' : '50%'};
`;

const ButtonGroup = styled.div`
  display: flex;
  width: 100%;
`;

const Button = styled.button`
  padding: 5px 10px;
  background-color: #007bff;
  color: white;
  font-size: 11px;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  margin: 0 1px;

  &:hover {
    background-color: #0056b3;
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const Heading = styled.h3`
  margin: 0;
  margin-top: 10px;
  padding-bottom: 5px;
`;

const Paragraph = styled.p`
  padding-top: 10px;
  margin: 0 0 20px 0;
  font-size:15px;
`;

export default function Reports({ colour, email }) {
  const [linkedAccounts, setLinkedAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [reports, setReports] = useState([]);
  const [reportCount, setReportCount] = useState(0);
  const [pagingTracker, setPagingTracker] = useState(
    {
      currentPage: 1,
      pageSize: 10,
      filterStatus: null,
      searchValue: null,
      account: null,
      coWorker: null
    });

  useEffect(() => {
    const loadLinkedAccounts = async () => {
      setIsLoading(true);
      try {
        const accounts = await fetchLinkedAccounts(email);
        const userAccounts = [];

        accounts.forEach(account => {
          userAccounts.push({ label: account.accountdescrip, value: account.accountid });
        });

        setLinkedAccounts(userAccounts);
        setError(null);
      } catch (e) {
        setError(e);
      } finally {
        setIsLoading(false);
      }
    };

    loadLinkedAccounts();
  }, [email]);

  const loadReports = useCallback(async (account, page, pageSize, filterStatus = null, searchValue = null, coWorker = null) => {
    setIsLoading(true);

    if (account || linkedAccounts) {
      try {
        const fetchedReports = await fetchReports((account ? account : linkedAccounts[0].value), page, pageSize);

        if (!fetchedReports[0].PDFReportsResponse.length) {
          setReports([]);
          setReportCount(0);
        }

        if (fetchedReports && fetchedReports[0].PDFReportsResponse) {
          const restructuredReports = fetchedReports[0].PDFReportsResponse.map(report => ({
            ...report,
          }));

          setReports(restructuredReports);
          setReportCount(fetchedReports[0].Count);

        }

        setError(null);
      } catch (e) {
        setError(e);
        setReports([]);
          setReportCount(0);
      } finally {
        setIsLoading(false);
      }
    }
  }, [email, linkedAccounts]);


  const renderReport = async (report) => {
    setIsLoading(true);
    try {
      const pdfBlob = await fetchPdf(report.unquid, report.filename);

      const url = window.URL.createObjectURL(pdfBlob);

      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `${report.reporttype}_${report.accountid}_${formatDateToDDMMYYYY(report.datecreated)}` 

      document.body.appendChild(a);
      a.click();

      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setIsLoading(false);
    }
  };


  const columns = useMemo(() => [
    {
      Header: "Report Type",
      accessor: "reporttype",
    },
    {
      Header: "Account",
      accessor: "accountid",
    },
    {
      Header: "Date Created",
      accessor: row => formatDateToDDMMYYYY(row.datecreated),
    },
    {
      Header: "Actions",
      accessor: "actions",
      Cell: ({ row }) => (
        <ButtonGroup>
          <Button onClick={() => renderReport(row.original)}>
            Download
          </Button>
        </ButtonGroup>

      )
    },
  ]
  );

  return (
    <CenterContainer>
      <FormContainer>
        <Card
          label={'Electronic Order Reports'}
          headercolour={colour.sidebar}
          headerTextColour={colour.font}>
          <Heading>Reporting</Heading>
          <Paragraph>Use the table below to view, and download your reports.</Paragraph>
          <ServerSidePagingTable
            columns={columns}
            pagingFn={loadReports}
            data={reports}
            recordCount={reportCount}
            setPagingTracker={setPagingTracker}
            arrAccountFilters={linkedAccounts}
            showSearchField={false} />

          {(error && !linkedAccounts) && <Alert type="error">Unable to render page due to <b>{error.message}</b></Alert>}
          {!reportCount && <Alert type="information">No Reports found.</Alert>}
        </Card>
      </FormContainer>
    </CenterContainer>
  );
}